import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const RED = "#e02f2f";
const YELLOW = "#f2b21a";
const GREEN = "#3fbf5f";
const GRAY = "#9aa0a6";
function focalPx(fovDeg, pixels) {
    const fov = (fovDeg * Math.PI) / 180;
    return pixels / (2 * Math.tan(fov / 2));
}
function makeCamera(o) {
    const { h, pitchDeg, fovHorizontalDeg, fovVerticalDeg, W, H } = o;
    // Паспортные FOV используются напрямую.
    const fx = focalPx(fovHorizontalDeg, W);
    const fy = focalPx(fovVerticalDeg, H);
    const cx = W / 2;
    const cy = H / 2;
    const p = (pitchDeg * Math.PI) / 180;
    const cp = Math.cos(p);
    const sp = Math.sin(p);
    // g — горизонтальное расстояние по земле
    // от надира камеры.
    const project = (X, g) => {
        const Yc = -h;
        const Yr = cp * Yc + sp * g;
        const Zr = sp * h + cp * g;
        if (Zr <= 1e-4) {
            return {
                x: NaN,
                y: NaN,
                ok: false,
            };
        }
        return {
            x: cx + fx * (X / Zr),
            y: cy - fy * (Yr / Zr),
            ok: true,
        };
    };
    // Горизонт определяется вертикальной проекцией,
    // поэтому здесь используется fy.
    const horizonY = cy - fy * Math.tan(p);
    return {
        project,
        horizonY,
        fx,
        fy,
    };
}
/** Клиппинг полилинии по кадру (с запасом для дотягивания до краёв). */
function clip(pts, W, H) {
    const m = Math.max(W, H);
    const out = [];
    let cur = [];
    for (const p of pts) {
        const ok = p.ok && p.x > -m && p.x < W + m && p.y > -m && p.y < H + m;
        if (ok)
            cur.push({ x: p.x, y: p.y });
        else {
            if (cur.length >= 2)
                out.push(cur);
            cur = [];
        }
    }
    if (cur.length >= 2)
        out.push(cur);
    return out;
}
// Цвет по ФИКСИРОВАННЫМ порогам расстояния (метры), не по долям длины.
// Красная зона ВСЕГДА 0–2 м (ближайшее расстояние до машины), жёстко.
const RED_MAX_METERS = 2;
function zoneColor(g, yellowMax) {
    if (g < RED_MAX_METERS)
        return RED;
    if (g < yellowMax)
        return YELLOW;
    return GREEN;
}
export const ParkingGrid = ({ heightMeters = 1.5, pitchDeg = 15, fovHorizontalDeg = 97, fovVerticalDeg = 52.2, frameWidth = 1920, frameHeight = 1080, lengthMeters = 8, widthMeters = 2, stepMeters = 2, yellowMaxMeters = 4, tickMeters = 0.4, showLabels = false, showHorizon = false, opacity = 1, preserveAspectRatio = "none", className, style, }) => {
    const W = frameWidth;
    const H = frameHeight;
    const cam = makeCamera({
        h: heightMeters,
        pitchDeg,
        fovHorizontalDeg,
        fovVerticalDeg,
        W,
        H,
    });
    const half = widthMeters / 2;
    const tick = Math.min(tickMeters, widthMeters / 2);
    const strokeMain = Math.max(2, W / 380);
    const strokeTick = Math.max(2, W / 460);
    const fontSize = Math.max(12, W / 80);
    const elements = [];
    let key = 0;
    // Продольные борта (левый/правый), окрашенные по зонам, с клиппингом.
    const N = 160;
    for (const side of [-half, half]) {
        for (let i = 0; i < N; i++) {
            const g0 = (i / N) * lengthMeters;
            const g1 = ((i + 1) / N) * lengthMeters;
            const seg = clip([cam.project(side, g0), cam.project(side, g1)], W, H);
            const col = zoneColor((g0 + g1) / 2, yellowMaxMeters);
            for (const pc of seg) {
                if (pc.length < 2)
                    continue;
                elements.push(_jsx("line", { x1: pc[0].x, y1: pc[0].y, x2: pc[1].x, y2: pc[1].y, stroke: col, strokeWidth: strokeMain }, key++));
            }
        }
    }
    // Короткие засечки на кратных stepMeters (+ дальний край): у каждого борта,
    // направлены ВНУТРЬ на tick метров.
    const marks = [];
    for (let g = stepMeters; g <= lengthMeters + 1e-6; g += stepMeters) {
        marks.push(Math.min(g, lengthMeters));
    }
    if (marks.length === 0 ||
        Math.abs(marks[marks.length - 1] - lengthMeters) > 1e-6) {
        marks.push(lengthMeters);
    }
    for (const g of marks) {
        const col = zoneColor(g, yellowMaxMeters);
        // левый борт: от -half внутрь к -half+tick; правый: от half к half-tick
        const ticks = [
            [-half, -half + tick],
            [half, half - tick],
        ];
        for (const [x0, x1] of ticks) {
            const seg = clip([cam.project(x0, g), cam.project(x1, g)], W, H);
            for (const pc of seg) {
                if (pc.length < 2)
                    continue;
                elements.push(_jsx("line", { x1: pc[0].x, y1: pc[0].y, x2: pc[1].x, y2: pc[1].y, stroke: col, strokeWidth: strokeTick }, key++));
            }
        }
        if (showLabels) {
            const rp = cam.project(half, g);
            if (rp.ok && rp.y > 0 && rp.y < H) {
                elements.push(_jsxs("text", { x: rp.x + 12, y: rp.y + 5, fill: col, fontSize: fontSize, fontFamily: "system-ui, sans-serif", stroke: "none", children: [g % 1 === 0 ? g : g.toFixed(1), "\u043C"] }, key++));
            }
        }
    }
    if (showHorizon && cam.horizonY > 0 && cam.horizonY < H) {
        elements.push(_jsx("line", { x1: 0, y1: cam.horizonY, x2: W, y2: cam.horizonY, stroke: GRAY, strokeWidth: 1, strokeDasharray: "8 8" }, key++));
    }
    return (_jsx("svg", { className: className, style: Object.assign({ opacity, display: "block", width: "100%", height: "100%" }, style), viewBox: `0 0 ${W} ${H}`, preserveAspectRatio: preserveAspectRatio, xmlns: "http://www.w3.org/2000/svg", children: _jsx("g", { fill: "none", strokeLinecap: "round", strokeLinejoin: "round", children: elements }) }));
};
export default ParkingGrid;
